import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonSelectOption } from '@ionic/angular/standalone';
import { SelectComponent } from '../select/select.component';
import { TenantSelectionService } from '../../services/tenant-selection.service';
import { TenantService } from '../../services/tenant.service';
import { Subscription, combineLatest, of } from 'rxjs';

/**
 * Component for selecting/switching tenants in multi-tenant scenarios.
 * Only visible when user has multiple tenants.
 */
@Component({
  selector: 'crowdctrl-tenant-selector',
  standalone: true,
  imports: [CommonModule, SelectComponent, IonSelectOption],
  template: `
    @if (isMultiTenant && availableTenants.length > 0) {
      <crowdctrl-select
        [value]="selectedTenantId"
        (ionChange)="onTenantChange($event)"
        [interfaceOptions]="{ header: 'Select Tenant' }"
        placeholder="Select Tenant"
      >
        @for (tenant of availableTenants; track tenant.id) {
          <ion-select-option [value]="tenant.id">
            {{ tenant.name }}
          </ion-select-option>
        }
      </crowdctrl-select>
    }
  `,
  styles: [
    `
      crowdctrl-select {
        max-width: 200px;
      }
    `,
  ],
})
export class TenantSelectorComponent implements OnInit, OnDestroy {
  private tenantSelectionService = inject(TenantSelectionService);
  private tenantService = inject(TenantService);

  isMultiTenant = false;
  availableTenants: Array<{ id: string; name: string }> = [];
  selectedTenantId: string | null = null;

  private subscriptions = new Subscription();

  async ngOnInit(): Promise<void> {
    // Check if user is multi-tenant
    this.isMultiTenant = await this.tenantSelectionService.isMultiTenant();

    if (this.isMultiTenant) {
      // Get available tenant IDs
      const tenantIds = await this.tenantSelectionService.getAvailableTenants();

      if (tenantIds && tenantIds.length > 0) {
        // Fetch tenant details for each tenant ID
        const tenantPromises = tenantIds.map(async (id) => {
          try {
            const response = await this.tenantService.getTenant(id).toPromise();
            if (response && response.tenant) {
              return { id: response.tenant.id, name: response.tenant.name };
            }
            return null;
          } catch (error) {
            console.error(`Error loading tenant ${id}:`, error);
            return null;
          }
        });

        const tenants = await Promise.all(tenantPromises);
        this.availableTenants = tenants.filter((t) => t !== null) as Array<{
          id: string;
          name: string;
        }>;

        // Subscribe to selected tenant changes
        this.subscriptions.add(
          this.tenantSelectionService.selectedTenantId$.subscribe((id) => {
            this.selectedTenantId = id;
          }),
        );

        // Set initial selected tenant if not set
        if (!this.selectedTenantId && this.availableTenants.length > 0) {
          this.tenantSelectionService.setSelectedTenantId(this.availableTenants[0].id);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async onTenantChange(event: Event): Promise<void> {
    const tenantId = (event as CustomEvent<{ value?: string | null }>).detail.value;
    if (tenantId) {
      this.tenantSelectionService.setSelectedTenantId(tenantId);
      // Reload the page to refresh data with new tenant context
      window.location.reload();
    }
  }
}
